import { Theme } from '@mui/material';

import { darken, transparentize } from 'color2k';
import { memoize } from 'lodash';

import { IconButtonVariant } from './types';

import { Style } from '../../common/types';

type IconButtonStyleProps = { theme: Theme };

type IconButtonVariantStyle = { [key in IconButtonVariant]: Style };

type IconButtonLoadingStyle = { loading: IconButtonVariantStyle };

type IconButtonDisabledStyle = { disabled: IconButtonVariantStyle };

export type IconButtonStyle = IconButtonVariantStyle &
  IconButtonLoadingStyle &
  IconButtonDisabledStyle;

const hue = 500;

const hover: { [key in IconButtonVariant]: number } = {
  contained: 0.1,
  outlined: 0.9,
  icon: 0.9
};
const active: { [key in IconButtonVariant]: number } = {
  contained: 0.15,
  outlined: 0.85,
  icon: 0.85
};

export default memoize(
  ({ theme }: IconButtonStyleProps): IconButtonStyle => ({
    contained: {
      '&.MuiIconButton-colorPrimary': {
        'background': theme.palette.primary.main,
        'backgroundColor': theme.palette.primary.main,
        'borderColor': theme.palette.primary.main,
        'color': '#FFF',

        '&:hover': {
          background: darken(theme.palette.primary.main, hover.contained),
          backgroundColor: darken(theme.palette.primary.main, hover.contained),
          borderColor: darken(theme.palette.primary.main, hover.contained),
          color: '#FFF'
        },

        '&:active': {
          background: darken(theme.palette.primary.main, active.contained),
          backgroundColor: darken(theme.palette.primary.main, active.contained),
          borderColor: darken(theme.palette.primary.main, active.contained),
          color: '#FFF'
        }
      },
      '&.MuiIconButton-colorSecondary': {
        'background': theme.palette.grey[hue],
        'backgroundColor': theme.palette.grey[hue],
        'borderColor': theme.palette.grey[hue],
        'color': '#FFF',

        '&:hover': {
          background: darken(theme.palette.grey[hue], hover.contained),
          backgroundColor: darken(theme.palette.grey[hue], hover.contained),
          borderColor: darken(theme.palette.grey[hue], hover.contained),
          color: '#FFF'
        },

        '&:active': {
          background: darken(theme.palette.grey[hue], active.contained),
          backgroundColor: darken(theme.palette.grey[hue], active.contained),
          borderColor: darken(theme.palette.grey[hue], active.contained),
          color: '#FFF'
        }
      },
      '&.MuiIconButton-colorSuccess': {
        'background': theme.palette.success.main,
        'backgroundColor': theme.palette.success.main,
        'borderColor': theme.palette.success.main,
        'color': '#FFF',

        '&:hover': {
          background: darken(theme.palette.success.main, hover.contained),
          backgroundColor: darken(theme.palette.success.main, hover.contained),
          borderColor: darken(theme.palette.success.main, hover.contained),
          color: '#FFF'
        },

        '&:active': {
          background: darken(theme.palette.success.main, active.contained),
          backgroundColor: darken(theme.palette.success.main, active.contained),
          borderColor: darken(theme.palette.success.main, active.contained),
          color: '#FFF'
        }
      },
      '&.MuiIconButton-colorError': {
        'background': theme.palette.error.main,
        'backgroundColor': theme.palette.error.main,
        'borderColor': theme.palette.error.main,
        'color': '#FFF',

        '&:hover': {
          background: darken(theme.palette.error.main, hover.contained),
          backgroundColor: darken(theme.palette.error.main, hover.contained),
          borderColor: darken(theme.palette.error.main, hover.contained),
          color: '#FFF'
        },

        '&:active': {
          background: darken(theme.palette.error.main, active.contained),
          backgroundColor: darken(theme.palette.error.main, active.contained),
          borderColor: darken(theme.palette.error.main, active.contained),
          color: '#FFF'
        }
      },
      '&.MuiIconButton-colorInfo': {
        'background': theme.palette.info.main,
        'backgroundColor': theme.palette.info.main,
        'borderColor': theme.palette.info.main,
        'color': '#FFF',

        '&:hover': {
          background: darken(theme.palette.info.main, hover.contained),
          backgroundColor: darken(theme.palette.info.main, hover.contained),
          borderColor: darken(theme.palette.info.main, hover.contained),
          color: '#FFF'
        },

        '&:active': {
          background: darken(theme.palette.info.main, active.contained),
          backgroundColor: darken(theme.palette.info.main, active.contained),
          borderColor: darken(theme.palette.info.main, active.contained),
          color: '#FFF'
        }
      },
      '&.MuiIconButton-colorWarning': {
        'background': theme.palette.warning.main,
        'backgroundColor': theme.palette.warning.main,
        'borderColor': theme.palette.warning.main,
        'color': '#FFF',

        '&:hover': {
          background: darken(theme.palette.warning.main, hover.contained),
          backgroundColor: darken(theme.palette.warning.main, hover.contained),
          borderColor: darken(theme.palette.warning.main, hover.contained),
          color: '#FFF'
        },

        '&:active': {
          background: darken(theme.palette.warning.main, active.contained),
          backgroundColor: darken(theme.palette.warning.main, active.contained),
          borderColor: darken(theme.palette.warning.main, active.contained),
          color: '#FFF'
        }
      },

      '&.MuiIconButton-sizeSmall': {
        'borderWidth': '1px',

        '&:hover': {
          borderWidth: '1px'
        },

        '&:active': {
          borderWidth: '1px'
        },

        '&.Mui-disabled': {
          'borderWidth': '1px',

          '&:hover': {
            borderWidth: '1px'
          },

          '&:active': {
            borderWidth: '1px'
          }
        }
      },
      '&.MuiIconButton-sizeMedium': {
        'borderWidth': '2px',

        '&:hover': {
          borderWidth: '2px'
        },

        '&:active': {
          borderWidth: '2px'
        },

        '&.Mui-disabled': {
          'borderWidth': '2px',

          '&:hover': {
            borderWidth: '2px'
          },

          '&:active': {
            borderWidth: '2px'
          }
        }
      },
      '&.MuiIconButton-sizeLarge': {
        'borderWidth': '2px',

        '&:hover': {
          borderWidth: '2px'
        },

        '&:active': {
          borderWidth: '2px'
        },

        '&.Mui-disabled': {
          'borderWidth': '2px',

          '&:hover': {
            borderWidth: '2px'
          },

          '&:active': {
            borderWidth: '2px'
          }
        }
      }
    },
    outlined: {
      '&.MuiIconButton-colorPrimary': {
        'background': 'transparent',
        'backgroundColor': 'transparent',
        'borderColor': theme.palette.primary.main,
        'color': theme.palette.primary.main,

        '&:hover': {
          background: transparentize(theme.palette.primary.main, hover.outlined),
          backgroundColor: transparentize(theme.palette.primary.main, hover.outlined),
          borderColor: theme.palette.primary.main,
          color: theme.palette.primary.main
        },

        '&:active': {
          background: transparentize(theme.palette.primary.main, active.outlined),
          backgroundColor: transparentize(theme.palette.primary.main, active.outlined),
          borderColor: theme.palette.primary.main,
          color: theme.palette.primary.main
        }
      },
      '&.MuiIconButton-colorSecondary': {
        'background': 'transparent',
        'backgroundColor': 'transparent',
        'borderColor': theme.palette.grey[hue],
        'color': theme.palette.grey[hue],

        '&:hover': {
          background: transparentize(theme.palette.grey[hue], hover.outlined),
          backgroundColor: transparentize(theme.palette.grey[hue], hover.outlined),
          borderColor: theme.palette.grey[hue],
          color: theme.palette.grey[hue]
        },

        '&:active': {
          background: transparentize(theme.palette.grey[hue], active.outlined),
          backgroundColor: transparentize(theme.palette.grey[hue], active.outlined),
          borderColor: theme.palette.grey[hue],
          color: theme.palette.grey[hue]
        }
      },
      '&.MuiIconButton-colorSuccess': {
        'background': 'transparent',
        'backgroundColor': 'transparent',
        'borderColor': theme.palette.success.main,
        'color': theme.palette.success.main,

        '&:hover': {
          background: transparentize(theme.palette.success.main, hover.outlined),
          backgroundColor: transparentize(theme.palette.success.main, hover.outlined),
          borderColor: theme.palette.success.main,
          color: theme.palette.success.main
        },

        '&:active': {
          background: transparentize(theme.palette.success.main, active.outlined),
          backgroundColor: transparentize(theme.palette.success.main, active.outlined),
          borderColor: theme.palette.success.main,
          color: theme.palette.success.main
        }
      },
      '&.MuiIconButton-colorError': {
        'background': 'transparent',
        'backgroundColor': 'transparent',
        'borderColor': theme.palette.error.main,
        'color': theme.palette.error.main,

        '&:hover': {
          background: transparentize(theme.palette.error.main, hover.outlined),
          backgroundColor: transparentize(theme.palette.error.main, hover.outlined),
          borderColor: theme.palette.error.main,
          color: theme.palette.error.main
        },

        '&:active': {
          background: transparentize(theme.palette.error.main, active.outlined),
          backgroundColor: transparentize(theme.palette.error.main, active.outlined),
          borderColor: theme.palette.error.main,
          color: theme.palette.error.main
        }
      },
      '&.MuiIconButton-colorInfo': {
        'background': 'transparent',
        'backgroundColor': 'transparent',
        'borderColor': theme.palette.info.main,
        'color': theme.palette.info.main,

        '&:hover': {
          background: transparentize(theme.palette.info.main, hover.outlined),
          backgroundColor: transparentize(theme.palette.info.main, hover.outlined),
          borderColor: theme.palette.info.main,
          color: theme.palette.info.main
        },

        '&:active': {
          background: transparentize(theme.palette.info.main, active.outlined),
          backgroundColor: transparentize(theme.palette.info.main, active.outlined),
          borderColor: theme.palette.info.main,
          color: theme.palette.info.main
        }
      },
      '&.MuiIconButton-colorWarning': {
        'background': 'transparent',
        'backgroundColor': 'transparent',
        'borderColor': theme.palette.warning.main,
        'color': theme.palette.warning.main,

        '&:hover': {
          background: transparentize(theme.palette.warning.main, hover.outlined),
          backgroundColor: transparentize(theme.palette.warning.main, hover.outlined),
          borderColor: theme.palette.warning.main,
          color: theme.palette.warning.main
        },

        '&:active': {
          background: transparentize(theme.palette.warning.main, active.outlined),
          backgroundColor: transparentize(theme.palette.warning.main, active.outlined),
          borderColor: theme.palette.warning.main,
          color: theme.palette.warning.main
        }
      },

      '&.MuiIconButton-sizeSmall': {
        'borderWidth': '1px',

        '&:hover': {
          borderWidth: '1px'
        },

        '&:active': {
          borderWidth: '1px'
        },

        '&.Mui-disabled': {
          'borderWidth': '1px',

          '&:hover': {
            borderWidth: '1px'
          },

          '&:active': {
            borderWidth: '1px'
          }
        }
      },
      '&.MuiIconButton-sizeMedium': {
        'borderWidth': '2px',

        '&:hover': {
          borderWidth: '2px'
        },

        '&:active': {
          borderWidth: '2px'
        },

        '&.Mui-disabled': {
          'borderWidth': '2px',

          '&:hover': {
            borderWidth: '2px'
          },

          '&:active': {
            borderWidth: '2px'
          }
        }
      },
      '&.MuiIconButton-sizeLarge': {
        'borderWidth': '2px',

        '&:hover': {
          borderWidth: '2px'
        },

        '&:active': {
          borderWidth: '2px'
        },

        '&.Mui-disabled': {
          'borderWidth': '2px',

          '&:hover': {
            borderWidth: '2px'
          },

          '&:active': {
            borderWidth: '2px'
          }
        }
      }
    },
    icon: {
      '&.MuiIconButton-colorPrimary': {
        'background': 'transparent',
        'backgroundColor': 'transparent',
        'borderColor': 'transparent',
        'color': theme.palette.primary.main,

        '&:hover': {
          background: transparentize(theme.palette.primary.main, hover.icon),
          backgroundColor: transparentize(theme.palette.primary.main, hover.icon),
          borderColor: 'transparent',
          color: theme.palette.primary.main
        },

        '&:active': {
          background: transparentize(theme.palette.primary.main, active.icon),
          backgroundColor: transparentize(theme.palette.primary.main, active.icon),
          borderColor: 'transparent',
          color: theme.palette.primary.main
        }
      },
      '&.MuiIconButton-colorSecondary': {
        'background': 'transparent',
        'backgroundColor': 'transparent',
        'borderColor': 'transparent',
        'color': theme.palette.grey[hue],

        '&:hover': {
          background: transparentize(theme.palette.grey[hue], hover.icon),
          backgroundColor: transparentize(theme.palette.grey[hue], hover.icon),
          borderColor: 'transparent',
          color: theme.palette.grey[hue]
        },

        '&:active': {
          background: transparentize(theme.palette.grey[hue], active.icon),
          backgroundColor: transparentize(theme.palette.grey[hue], active.icon),
          borderColor: 'transparent',
          color: theme.palette.grey[hue]
        }
      },
      '&.MuiIconButton-colorSuccess': {
        'background': 'transparent',
        'backgroundColor': 'transparent',
        'borderColor': 'transparent',
        'color': theme.palette.success.main,

        '&:hover': {
          background: transparentize(theme.palette.success.main, hover.icon),
          backgroundColor: transparentize(theme.palette.success.main, hover.icon),
          borderColor: 'transparent',
          color: theme.palette.success.main
        },

        '&:active': {
          background: transparentize(theme.palette.success.main, active.icon),
          backgroundColor: transparentize(theme.palette.success.main, active.icon),
          borderColor: 'transparent',
          color: theme.palette.success.main
        }
      },
      '&.MuiIconButton-colorError': {
        'background': 'transparent',
        'backgroundColor': 'transparent',
        'borderColor': 'transparent',
        'color': theme.palette.error.main,

        '&:hover': {
          background: transparentize(theme.palette.error.main, hover.icon),
          backgroundColor: transparentize(theme.palette.error.main, hover.icon),
          borderColor: 'transparent',
          color: theme.palette.error.main
        },

        '&:active': {
          background: transparentize(theme.palette.error.main, active.icon),
          backgroundColor: transparentize(theme.palette.error.main, active.icon),
          borderColor: 'transparent',
          color: theme.palette.error.main
        }
      },
      '&.MuiIconButton-colorInfo': {
        'background': 'transparent',
        'backgroundColor': 'transparent',
        'borderColor': 'transparent',
        'color': theme.palette.info.main,

        '&:hover': {
          background: transparentize(theme.palette.info.main, hover.icon),
          backgroundColor: transparentize(theme.palette.info.main, hover.icon),
          borderColor: 'transparent',
          color: theme.palette.info.main
        },

        '&:active': {
          background: transparentize(theme.palette.info.main, active.icon),
          backgroundColor: transparentize(theme.palette.info.main, active.icon),
          borderColor: 'transparent',
          color: theme.palette.info.main
        }
      },
      '&.MuiIconButton-colorWarning': {
        'background': 'transparent',
        'backgroundColor': 'transparent',
        'borderColor': 'transparent',
        'color': theme.palette.warning.main,

        '&:hover': {
          background: transparentize(theme.palette.warning.main, hover.icon),
          backgroundColor: transparentize(theme.palette.warning.main, hover.icon),
          borderColor: 'transparent',
          color: theme.palette.warning.main
        },

        '&:active': {
          background: transparentize(theme.palette.warning.main, active.icon),
          backgroundColor: transparentize(theme.palette.warning.main, active.icon),
          borderColor: 'transparent',
          color: theme.palette.warning.main
        }
      },

      '&.MuiIconButton-sizeSmall': {
        'borderWidth': '1px',

        '&:hover': {
          borderWidth: '1px'
        },

        '&:active': {
          borderWidth: '1px'
        }
      },
      '&.MuiIconButton-sizeMedium': {
        'borderWidth': '2px',

        '&:hover': {
          borderWidth: '2px'
        },

        '&:active': {
          borderWidth: '2px'
        }
      },
      '&.MuiIconButton-sizeLarge': {
        'borderWidth': '2px',

        '&:hover': {
          borderWidth: '2px'
        },

        '&:active': {
          borderWidth: '2px'
        }
      }
    },
    loading: {
      contained: {
        '&.MuiIconButton-root': {
          pointerEvents: 'none',

          background: theme.palette.grey[hue],
          backgroundColor: theme.palette.grey[hue],
          borderColor: theme.palette.grey[hue],
          color: '#FFF'
        }
      },
      outlined: {
        '&.MuiIconButton-root': {
          pointerEvents: 'none',

          background: 'transparent',
          backgroundColor: 'transparent',
          borderColor: theme.palette.grey[hue],
          color: theme.palette.grey[hue]
        }
      },
      icon: {
        '&.MuiIconButton-root': {
          pointerEvents: 'none',

          background: 'transparent',
          backgroundColor: 'transparent',
          borderColor: 'transparent',
          color: theme.palette.grey[hue]
        }
      }
    },
    disabled: {
      contained: {
        '&.Mui-disabled': {
          pointerEvents: 'none',

          background: theme.palette.grey[hue],
          backgroundColor: theme.palette.grey[hue],
          borderColor: theme.palette.grey[hue],
          color: '#FFF',

          opacity: 0.5
        }
      },
      outlined: {
        '&.Mui-disabled': {
          pointerEvents: 'none',

          background: 'transparent',
          backgroundColor: 'transparent',
          borderColor: theme.palette.grey[hue],
          color: theme.palette.grey[hue],

          opacity: 0.5
        }
      },
      icon: {
        '&.Mui-disabled': {
          pointerEvents: 'none',

          background: 'transparent',
          backgroundColor: 'transparent',
          borderColor: 'transparent',
          color: theme.palette.grey[hue],

          opacity: 0.5
        }
      }
    }
  })
);
